import { ref, computed } from 'vue';
import {
    getDeviceProps,
    getCustomEventProperties,
    getHistoricalMetadata,
    getAccountMetadata,
    getVisitorMetadata,
    getParentAccountMetadata,
    getSessionProperties,
    getLocationProps,
    getTopLevelProps,
    getPageParameterProps,
} from './utils';
import { filterEventPropList } from '@/vuex/modules/metadata/metadata.utils';
import { FilterType } from '@/types/analytics/analytics.types';

export function getEventPropertySelection ({
    field,
    group,
    kind,
    filterType,
    platform,
    metadata,
    historicalMetadata,
    sessionProperties = [],
    accountMetadata = [],
    visitorMetadata = [],
    parentAccountMetadata = [],
}) {
    let optionsList = [];

    switch (filterType) {
        case FilterType.Device:
            // If the current field does not match a device prop based off the current platforms, then the selection must be a mobile device prop.
            // We concat the mobile device prop list as a fallback in case the selection cannot be matched based off the provided platforms.
            optionsList = getDeviceProps({ platform }).concat(getDeviceProps({ platform: ['mobile'] }));
            break;
        case FilterType.Custom:
            optionsList = getCustomEventProperties(filterEventPropList({ metadata }));
            break;
        case FilterType.Account:
            optionsList = getAccountMetadata(accountMetadata);
            break;
        case FilterType.Visitor:
            optionsList = getVisitorMetadata(visitorMetadata);
            break;
        case FilterType.ParentAccount:
            optionsList = getParentAccountMetadata(parentAccountMetadata);
            break;
        case FilterType.PageParameter:
            optionsList = getPageParameterProps();
            break;
        case FilterType.Location:
            optionsList = getLocationProps();
            break;
        case FilterType.EventTopLevel:
            optionsList = getTopLevelProps();
            break;
        case FilterType.HistoricalMetadata:
            optionsList = getHistoricalMetadata(historicalMetadata);
            break;
        case FilterType.Utm:
            optionsList = getSessionProperties(sessionProperties);
    }

    return (
        optionsList.find(
            (ep) =>
                ep.eventPropertyKind === kind &&
                ep.eventPropertyField === field &&
                (ep?.eventPropertyGroup === undefined || ep.eventPropertyGroup === group),
        ) || {}
    );
}

export default function (
    filter,
    platforms,
    metadata,
    historicalMetadata,
    sessionProperties,
    accountMetadata,
    visitorMetadata,
    parentAccountMetadata,
) {
    const field = ref(filter.value.field);
    const name = ref(filter.value.name);
    const kind = ref(filter.value.kind);
    const group = ref(filter.value.group);
    const filterType = ref(filter.value.filterType);

    const eventPropertySelection = computed({
        get () {
            return getEventPropertySelection({
                field: field.value,
                group: group.value,
                kind: kind.value,
                filterType: filterType.value,
                platform: platforms.value,
                metadata: metadata.value,
                historicalMetadata: historicalMetadata.value,
                sessionProperties: sessionProperties?.value,
                accountMetadata: accountMetadata?.value,
                visitorMetadata: visitorMetadata?.value,
                parentAccountMetadata: parentAccountMetadata?.value,
            });
        },
        set (selection) {
            field.value = selection.eventPropertyField;
            name.value = selection.name;
            kind.value = selection.eventPropertyKind;
            group.value = selection.eventPropertyGroup;
            filterType.value = selection.filterType;
        },
    });

    return {
        field,
        name,
        kind,
        group,
        filterType,
        eventPropertySelection,
    };
}
